import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, CalendarDays, User } from "lucide-react";
import { api, fileUrl } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Empty from "@/components/Empty";

const FILTERS = ["All", "Company News", "Education", "Academic Learning", "Music Education", "Leadership", "Technology", "Events", "Press Releases"];

function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function News() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = filter === "All" ? articles : articles.filter((a) => a.category === filter);

  return (
    <div data-testid="news-page">
      <PageHero
        eyebrow="News & Insights"
        title="Perspectives from the RCS ecosystem."
        lede="Company announcements, education perspectives and stories from across The Topper Academy and Indian School of Music."
      />

      <section className="py-24 lg:py-32" data-testid="news-list">
        <div className="container-rcs">
          <div className="flex flex-wrap gap-2.5" data-testid="news-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`news-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                  filter === f
                    ? "bg-navy-950 text-gold"
                    : "border border-navy-900/15 bg-white text-navy-900 hover:border-gold-dark"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="mt-12">
            {visible.length === 0 ? (
              <Empty
                icon={Newspaper}
                title="Insights are on their way"
                text="News, perspectives and press releases from Roy Career Solutions will be published here soon."
                testid="news-empty"
              />
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {visible.map((a, i) => (
                  <Reveal key={a.id} delay={i * 0.08}>
                    <Link to={`/news/${a.slug}`} className="card-premium flex h-full flex-col !p-0 overflow-hidden" data-testid={`news-card-${a.slug}`}>
                      {a.image && (
                        <img src={fileUrl(a.image)} alt={a.title} loading="lazy" className="aspect-[16/9] w-full object-cover" />
                      )}
                      <div className="flex flex-1 flex-col p-7">
                        <span className="eyebrow">{a.category}</span>
                        <h3 className="mt-3 font-serif text-xl leading-snug text-navy-900">{a.title}</h3>
                        {a.excerpt && <p className="mt-3 text-sm leading-relaxed text-slate-600 line-clamp-3">{a.excerpt}</p>}
                        <div className="mt-auto flex items-center gap-5 pt-6 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" aria-hidden />{formatDate(a.date)}</span>
                          {a.author && <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" aria-hidden />{a.author}</span>}
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
